import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/Tabs/Tabs';
import { useErrorTranslation } from '@/hooks/useErrorTranslation';
import { captureAnalyticsEvent } from '@/lib/mixpanel';
import { useAuthStore } from '@/stores/authStore';
import type { Student } from '@/types/student';
import { useGetLastAppointment } from '../../api/getLastAppointment';
import { useGetStudentsSchedulingDetails } from '../../api/getStudentsSchedulingDetails';
import { useTimeFor } from '@/hooks/useTimeFor';
import { sortStudentsByActiveEnrollmentThenName } from '../../utils/sortStudentsByActiveEnrollmentThenName';
import { SessionOverviewEmpty } from '../SessionOverviewEmpty/SessionOverviewEmpty';
import { SessionOverviewError } from '../SessionOverviewError/SessionOverviewError';
import { SessionOverviewLoading } from '../SessionOverviewLoading/SessionOverviewLoading';
import { SessionsHistoryItem } from '../SessionsHistoryItem/SessionsHistoryItem';

export const LastSessionOverview = () => {
  const { t } = useTranslation('scheduling', { keyPrefix: 'lastSessionOverview' });
  const { translateError } = useErrorTranslation();

  const { userData } = useAuthStore();
  const { timeZone: centerTimeZone } = useTimeFor('sessionTime');
  const { data: schedulingDetails, isLoading: isResolvingTabOrder } = useGetStudentsSchedulingDetails({});

  const students = useMemo(
    () => sortStudentsByActiveEnrollmentThenName(userData?.students ?? [], schedulingDetails?.students, centerTimeZone),
    [userData?.students, schedulingDetails?.students, centerTimeZone],
  );

  const [pickedStudentId, setPickedStudentId] = useState<string | null>(null);
  const isOnAccount = (studentId: string | null) => students.some((student: Student) => student.id.toString() === studentId);
  const leftmostStudentId = students[0]?.id?.toString() ?? null;

  const selectedStudentId = isOnAccount(pickedStudentId) ? pickedStudentId : leftmostStudentId;

  const currentStudentId = isResolvingTabOrder ? null : selectedStudentId;

  const {
    data,
    isLoading: isLoadingLastSession,
    error,
  } = useGetLastAppointment({
    studentId: Number.parseInt(currentStudentId!, 10),
    options: { enabled: !!currentStudentId },
  });

  const hasNoSessionHistory = !data?.session;

  return (
    <section className='bg-fill-background-light flex flex-col gap-4 rounded-lg p-6' aria-busy={isResolvingTabOrder || isLoadingLastSession}>
      <h2 className='text-text-default-secondary'>{t('title')}</h2>
      {isResolvingTabOrder && <SessionOverviewLoading />}
      {!isResolvingTabOrder && students.length > 0 && (
        <Tabs className='w-full' value={currentStudentId!} onValueChange={setPickedStudentId}>
          {students.length > 1 && (
            <TabsList className='mb-7 flex w-full items-center justify-start'>
              {students.map((student) => (
                <TabsTrigger
                  key={student.id}
                  value={student.id?.toString()}
                  onClick={() => void captureAnalyticsEvent('lastsession_studentchange', { studentId: student.id })}
                >
                  {student.firstName}
                </TabsTrigger>
              ))}
            </TabsList>
          )}

          {isLoadingLastSession && <SessionOverviewLoading />}
          {!isLoadingLastSession && error && <SessionOverviewError>{translateError(error)}</SessionOverviewError>}
          {!isLoadingLastSession && !error && hasNoSessionHistory && <SessionOverviewEmpty>{t('empty')}</SessionOverviewEmpty>}

          {!isLoadingLastSession &&
            !error &&
            !hasNoSessionHistory &&
            students.map((student) => (
              <TabsContent key={student.id} value={student.id?.toString()} className='overflow-auto'>
                <SessionsHistoryItem session={data.session!} isAlwaysExpanded hideStudentInfo className='p-0' />
              </TabsContent>
            ))}
        </Tabs>
      )}
    </section>
  );
};
